import { useCallback, Dispatch, SetStateAction } from 'react';
import { GameState, Player, Morale } from '../../../domain/entities';
import { findPlayerInState, addNewsItem, getPlayerTeam } from '../../../services/GameService';

type SetGameState = Dispatch<SetStateAction<GameState | { gameStarted: false; }>>;

interface UsePlayerManagementProps {
    setGameState: SetGameState;
}

export const usePlayerManagement = ({ setGameState }: UsePlayerManagementProps) => {
    const renewContract = useCallback((playerId: number, newWage: number, newLength: number): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: 'Game not started.' });
                    return prevState;
                }

                const newState: GameState = JSON.parse(JSON.stringify(prevState));
                (newState as any).gameStarted = true;
                const playerInfo = findPlayerInState(newState, playerId);
                if (!playerInfo || playerInfo.team.name !== newState.playerTeamName) {
                    resolve({ success: false, message: 'Player not found in your team.' });
                    return prevState;
                }

                // Check per-player negotiation limit (max 2 per week)
                if (!newState.playerNegotiationsThisWeek) {
                    newState.playerNegotiationsThisWeek = {};
                }
                const negotiationsThisWeek = newState.playerNegotiationsThisWeek[playerId] || 0;
                if (negotiationsThisWeek >= 2) {
                    resolve({ success: false, message: `You can only negotiate with this player 2 times per week. Try again next week.` });
                    return prevState;
                }

                const { player, team } = playerInfo;
                const currentWage = player.wage;
                const wageIncreasePercent = (newWage - currentWage) / currentWage;
                let baseAcceptance = 0.5 + wageIncreasePercent * 2;
                const moraleValues: Record<string, number> = { 'Ecstatic': 0.2, 'Happy': 0.1, 'Content': 0, 'Unhappy': -0.2, 'Furious': -0.4 };
                baseAcceptance += moraleValues[player.morale] || 0;
                if (player.personality === 'Loyal') baseAcceptance += 0.15;
                if (player.personality === 'Ambitious') baseAcceptance -= 0.1;
                if (player.form > 1) baseAcceptance += 0.1;
                if (player.form < -1) baseAcceptance -= 0.1;
                const teamAvgSkill = team.squad.reduce((sum, p) => sum + p.skill, 0) / team.squad.length;
                if (player.skill > teamAvgSkill + 5) baseAcceptance -= 0.1;

                // Increment negotiation counter
                newState.playerNegotiationsThisWeek![playerId] = (newState.playerNegotiationsThisWeek![playerId] || 0) + 1;

                if (Math.random() < baseAcceptance) {
                    player.wage = newWage;
                    player.contract.yearsRemaining = newLength;
                    player.contract.expirySeason = newState.season + newLength;
                    player.morale = 'Happy';
                    player.moraleReason = 'Signed a new contract';
                    addNewsItem(newState, 'Contract Renewed', `${player.name} has signed a new ${newLength}-year contract with the club.`, 'general', undefined, { type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id });
                    resolve({ success: true, message: 'Player accepted the new contract!' });
                    return newState;
                } else {
                    player.morale = 'Furious';
                    player.moraleReason = 'Contract talks broke down';
                    addNewsItem(newState, 'Contract Talks Fail', `${player.name} has rejected the contract offer from the club.`, 'morale', undefined, { type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id });
                    resolve({ success: false, message: `${player.name} has rejected your offer.` });
                    return newState;
                }
            });
        });
    }, [setGameState]);

    const handlePlayerConversation = useCallback((playerId: number, choice: 'praise' | 'promise' | 'show_faith' | 'demand_improvement' | 'warn_dropped' | 'dismiss_concerns' | 'assert_authority'): Promise<{ success: boolean; message: string; }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: 'Game not started.' });
                    return prevState;
                }
                const newState: GameState = JSON.parse(JSON.stringify(prevState));
                (newState as any).gameStarted = true;
                const playerInfo = findPlayerInState(newState, playerId);
                if (!playerInfo) {
                    resolve({ success: false, message: 'Player not found.' });
                    return prevState;
                }
                const { player } = playerInfo;
                const WEEKS_PER_SEASON = newState.config.WEEKS_PER_SEASON;

                const isPlayerInitiated = !!player.wantsToTalk;
                const now = { season: newState.season, week: newState.currentWeek };

                // Check per-player cooldown
                const playerCooldown = player.nextConversationAvailable;
                const isPlayerOnCooldown = playerCooldown && (now.season < playerCooldown.season || (now.season === playerCooldown.season && now.week < playerCooldown.week));

                if (!isPlayerInitiated && isPlayerOnCooldown) {
                    resolve({ success: false, message: `You can't talk to this player again until week ${Math.floor(playerCooldown.week)} (Season ${playerCooldown.season}).` });
                    return prevState;
                }

                // Check weekly conversation limit (max 2 per week)
                const conversationsThisWeek = newState.playerConversationsThisWeek;
                const isNewWeek = !conversationsThisWeek || conversationsThisWeek.season !== now.season || conversationsThisWeek.week !== now.week;
                const currentWeekConversationCount = isNewWeek ? 0 : conversationsThisWeek.count;

                if (!isPlayerInitiated && currentWeekConversationCount >= 2) {
                    resolve({ success: false, message: `You can't talk to any more players until next week.` });
                    return prevState;
                }

                player.wantsToTalk = false; // Always clear this flag
                let outcomeMessage = '';
                let moraleChange = 0;
                let formChange = 0;

                // Diminishing returns/annoyance logic
                let effectivenessModifier = 1.0;
                if (player.lastManagerTalk) {
                    const weeksSince = (now.season - player.lastManagerTalk.season) * WEEKS_PER_SEASON + (now.week - player.lastManagerTalk.week);
                    if (weeksSince < 4) effectivenessModifier = 0.25;
                    else if (weeksSince < 8) effectivenessModifier = 0.5;

                    if (effectivenessModifier < 1.0) {
                        if (player.personality === 'Hot-headed' || player.personality === 'Ambitious') {
                            moraleChange -= 1; // Penalty for being bothered again
                            outcomeMessage += `${player.name} seems tired of these frequent chats. `;
                        } else if (player.personality === 'Professional') {
                            effectivenessModifier = Math.min(1.0, effectivenessModifier * 1.5); // Professionals are less affected
                        }
                    }
                }

                const moraleMap: Record<Morale, number> = { 'Ecstatic': 5, 'Happy': 4, 'Content': 3, 'Unhappy': 2, 'Furious': 1 };
                const numberToMorale: Record<number, Morale> = { 5: 'Ecstatic', 4: 'Happy', 3: 'Content', 2: 'Unhappy', 1: 'Furious' };
                const changeMorale = (current: Morale, change: number) => {
                    const newLevel = Math.max(1, Math.min(5, moraleMap[current] + change));
                    return numberToMorale[newLevel];
                };

                // Conversation logic based on player's issue
                if (player.moraleReason !== 'Poor recent performance') { // Non-performance issues
                    switch (choice) {
                        case 'praise': moraleChange += (player.personality === 'Professional' || player.personality === 'Loyal' ? 2 : 1); player.moraleReason = 'Encouraged by manager praise'; outcomeMessage += `${player.name} appreciates your words of encouragement.`; break;
                        case 'promise': moraleChange += (player.personality === 'Ambitious' ? 2 : 1); player.moraleReason = 'Hopeful for more game time'; outcomeMessage += `${player.name} is hopeful about getting more game time in the future.`; break;
                        case 'dismiss_concerns': player.moraleReason = "Manager dismissed concerns"; if (player.personality === 'Professional') { moraleChange += 1; outcomeMessage += `${player.name} respects your directness and is ready to focus.`; } else if (player.personality === 'Loyal') { outcomeMessage += `${player.name} is a little disappointed but accepts your decision.`; } else { moraleChange -= 1; outcomeMessage += `${player.name} is frustrated that their concerns were dismissed.`; } break;
                        case 'assert_authority': player.moraleReason = "Clashed with manager's authority"; if (player.personality === 'Loyal') { moraleChange += 1; outcomeMessage += `${player.name} respects your authority and the club-first mentality.`; } else if (player.personality === 'Hot-headed') { moraleChange -= 2; outcomeMessage += `${player.name} reacts angrily to being told they are not bigger than the club.`; } else { moraleChange -= 2; outcomeMessage += `${player.name} feels alienated by your authoritarian stance.`; } break;
                    }
                } else { // Performance issues
                    switch (choice) {
                        case 'show_faith': moraleChange += (player.personality === 'Loyal' || player.personality === 'Professional') ? 2 : 1; if (player.personality === 'Ambitious') moraleChange--; outcomeMessage = `${player.name} appreciates your backing.`; player.moraleReason = 'Feels backed by the manager'; break;
                        case 'demand_improvement': if (player.personality === 'Professional' || player.personality === 'Ambitious') { moraleChange++; formChange++; } else if (player.personality === 'Hot-headed') moraleChange--; outcomeMessage = `You demand more from ${player.name}.`; player.moraleReason = 'Challenged to improve by manager'; break;
                        case 'warn_dropped': if (player.personality === 'Ambitious') formChange += 2; else if (player.personality === 'Professional') formChange++; moraleChange--; if (player.personality === 'Hot-headed' || player.personality === 'Loyal') moraleChange--; outcomeMessage = `You warn ${player.name} that their place is at risk.`; player.moraleReason = 'Worried about being dropped'; break;
                    }
                }

                // Apply effectiveness modifier to positive changes
                if (moraleChange > 0) moraleChange = Math.round(moraleChange * effectivenessModifier);
                if (formChange > 0) formChange = Math.round(formChange * effectivenessModifier);

                player.morale = changeMorale(player.morale, moraleChange);
                player.form = Math.max(-3, Math.min(3, player.form + formChange));

                // Set per-player cooldown (4 weeks from now)
                let cooldownWeek = now.week + 4;
                let cooldownSeason = now.season;
                if (cooldownWeek > WEEKS_PER_SEASON) {
                    cooldownWeek -= WEEKS_PER_SEASON;
                    cooldownSeason += 1;
                }
                player.nextConversationAvailable = { season: cooldownSeason, week: cooldownWeek };

                // Update weekly conversation counter
                if (isNewWeek) {
                    newState.playerConversationsThisWeek = { season: now.season, week: now.week, count: 1 };
                } else {
                    newState.playerConversationsThisWeek!.count++;
                }

                // Set last talk time on player
                player.lastManagerTalk = { season: now.season, week: now.week };

                addNewsItem(newState, 'Player Conversation', outcomeMessage, 'morale', undefined, { type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id });
                resolve({ success: moraleChange >= 0, message: outcomeMessage });
                return newState;
            });
        });
    }, [setGameState]);
    
    const offerFinancialIncentive = useCallback((playerId: number, type: 'wage' | 'bonus', amount: number): Promise<{ success: boolean; message: string; }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) { resolve({ success: false, message: 'Game not started.' }); return prevState; }
                const newState: GameState = JSON.parse(JSON.stringify(prevState));
                (newState as any).gameStarted = true;
                const playerInfo = findPlayerInState(newState, playerId);
                const playerTeam = getPlayerTeam(newState);
                if (!playerInfo || playerInfo.team.name !== playerTeam.name) { resolve({ success: false, message: 'Player not found in your team.' }); return prevState; }
                const { player } = playerInfo;
                if (type === 'bonus') {
                    if (playerTeam.finances.balance < amount) { resolve({ success: false, message: 'Not enough funds to pay this bonus.' }); return prevState; }
                    playerTeam.finances.balance -= amount;
                    playerTeam.finances.history.push({ season: newState.season, week: newState.currentWeek, event: `Bonus paid to ${player.name}`, amount: -amount });
                } else { 
                    const oldWage = player.wage;
                    const newWage = amount;
                    const weeklyIncrease = newWage - oldWage;
                    if (playerTeam.finances.balance < weeklyIncrease * 16) { resolve({ success: false, message: 'Not enough funds in the wage budget to sustain this increase.' }); return prevState; }
                    player.wage = newWage;
                }
                let moraleChange = (type === 'wage') ? 2 : 1;
                if (player.personality === 'Loyal' && type === 'wage') moraleChange++;
                if (player.personality === 'Ambitious' && player.moraleReason === 'Lack of game time') moraleChange--;
                if (player.moraleReason === 'Concerned about contract') moraleChange++;
                const moraleMap: Record<Morale, number> = { 'Ecstatic': 5, 'Happy': 4, 'Content': 3, 'Unhappy': 2, 'Furious': 1 };
                const numberToMorale: Record<number, Morale> = { 5: 'Ecstatic', 4: 'Happy', 3: 'Content', 2: 'Unhappy', 1: 'Furious' };
                player.morale = numberToMorale[Math.max(1, Math.min(5, moraleMap[player.morale] + moraleChange))];
                player.wantsToTalk = false;
                let outcomeMessage = '';
                if (type === 'wage') {
                    player.moraleReason = 'Pleased with new wage';
                    outcomeMessage = `${player.name} is happy with their new wage of £${player.wage.toLocaleString()}/wk. Their morale has improved.`;
                    addNewsItem(newState, 'Player Agrees New Terms', `${player.name} has agreed to a new wage following a private discussion with the manager.`, 'general');
                } else {
                    player.moraleReason = 'Appreciates manager bonus';
                    outcomeMessage = `${player.name} appreciates the bonus of £${amount.toLocaleString()}. Their morale has improved.`;
                    addNewsItem(newState, 'Player Receives Bonus', `${player.name} was awarded a one-off bonus by the manager to boost morale.`, 'finance');
                }
                resolve({ success: true, message: outcomeMessage });
                return newState;
            });
        });
    }, [setGameState]);

    return {
        renewContract,
        handlePlayerConversation,
        offerFinancialIncentive,
    };
};
